import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HiOutlineQuestionMarkCircle, HiOutlineBell } from 'react-icons/hi2';
import { useAuthStore } from '../../stores/authStore';
import { useEstimationStore } from '../../stores/estimationStore';
import { useGuideStore } from '../../stores/guideStore';
import { useNotificationStore } from '../../stores/notificationStore';
import { NotificationPanel } from '../notifications/NotificationPanel';
import styles from './Header.module.css';

const LANGUAGES = [
  { code: 'fr', label: 'FR' },
  { code: 'de', label: 'DE' },
  { code: 'it', label: 'IT' },
  { code: 'en', label: 'EN' },
];

export function Header() {
  const { i18n, t } = useTranslation();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const niveau = useEstimationStore((s) => s.niveau);
  const setNiveau = useEstimationStore((s) => s.setNiveau);
  const toggleGuide = useGuideStore((s) => s.toggleGuide);
  const unreadCount = useNotificationStore((s) => s.unreadCount);
  const toggleNotifications = useNotificationStore((s) => s.togglePanel);
  const startPolling = useNotificationStore((s) => s.startPolling);
  const stopPolling = useNotificationStore((s) => s.stopPolling);

  const isProjectDetail = /^\/app\/projects\/[^/]+/.test(location.pathname) && location.pathname !== '/app/projects';

  // Start polling for notifications when user is logged in
  useEffect(() => {
    if (user) {
      startPolling();
      return () => stopPolling();
    }
  }, [user, startPolling, stopPolling]);

  return (
    <header className={styles.header}>
      <div className={styles.left}>
        <h1 className={styles.logo}>QENQ</h1>
        {isProjectDetail && (
          <div className={styles.niveauToggle}>
            <button
              className={`${styles.nBtn} ${niveau === 1 ? styles.active : ''}`}
              onClick={() => setNiveau(1)}
            >
              {t('common:niveau1', 'Niveau 1')}
            </button>
            <button
              className={`${styles.nBtn} ${niveau === 2 ? styles.active : ''}`}
              onClick={() => setNiveau(2)}
            >
              {t('common:niveau2', 'Niveau 2')}
            </button>
          </div>
        )}
      </div>

      <div className={styles.right}>
        {user && (
          <button
            className={styles.bellBtn}
            onClick={toggleNotifications}
            aria-label="Notifications"
            title={`Notifications${unreadCount > 0 ? ` (${unreadCount})` : ''}`}
          >
            <HiOutlineBell />
            {unreadCount > 0 && (
              <span className={styles.badge}>
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>
        )}
        <NotificationPanel />
        <button
          className={styles.helpBtn}
          onClick={toggleGuide}
          aria-label="User Guide"
          title="User Guide (Ctrl+/)"
        >
          <HiOutlineQuestionMarkCircle />
        </button>
        <div className={styles.langSelect}>
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              className={`${styles.langBtn} ${i18n.language === lang.code ? styles.activeLang : ''}`}
              onClick={() => i18n.changeLanguage(lang.code)}
            >
              {lang.label}
            </button>
          ))}
        </div>
        {user && (
          <div className={styles.user}>
            <span className={styles.userName}>{user.full_name}</span>
            <button className={styles.logoutBtn} onClick={logout}>
              {t('common:logout', 'Logout')}
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
